import { useCallback, useState } from "react";
import { mapboxApi } from "../../../apis/mapbox/api";
import { MapBoxSearchApiSuggestEntry } from "../../../types/mapbox";
import { LocationParams } from "../../../types/drayage";
import { useRequiredData } from "../../../contextApis/RequiredDataContext";
import { MapboxSearchApiRetrieveResponse } from "../../../apis/mapbox/types";

/* global console */

export interface UseMapboxSearchProps {
  onLocationSelect: (location: LocationParams) => void;
}

const useMapboxSearch = ({ onLocationSelect }: UseMapboxSearchProps) => {
  const { appInstanceId, mapBoxConfig } = useRequiredData();
  const [searchValue, setSearchValue] = useState("");
  const [suggestions, setSuggestions] = useState<MapBoxSearchApiSuggestEntry[]>(
    [],
  );

  const formatSuggestionDisplay = (
    suggestion: MapBoxSearchApiSuggestEntry,
  ): {
    primary: string;
    secondary: string;
    tertiary: string;
  } => {
    const { feature_type, name, context } = suggestion;

    switch (feature_type) {
      case "address":
        return {
          primary: name,
          secondary: [
            context.place?.name,
            context.region?.region_code,
            context.postcode?.name,
          ]
            .filter(Boolean)
            .join(", "),
          tertiary: [
            name,
            context.place?.name,
            context.region?.region_code,
            context.postcode?.name,
          ]
            .filter(Boolean)
            .join(", "),
        };

      case "street":
        return {
          primary: name,
          secondary: [
            context.place?.name,
            context.region?.region_code,
            context.postcode?.name,
          ]
            .filter(Boolean)
            .join(", "),
          tertiary: [
            name,
            context.place?.name,
            context.region?.region_code,
            context.postcode?.name,
          ]
            .filter(Boolean)
            .join(", "),
        };

      case "place":
        return {
          primary: name,
          secondary: [context.region?.name].filter(Boolean).join(", "),
          tertiary: [name, context.region?.name].filter(Boolean).join(", "),
        };

      case "postcode":
        return {
          primary: `${context.place?.name || ""} ${name}`,
          secondary: [context.region?.name].filter(Boolean).join(", "),
          tertiary: [
            `${context.place?.name || ""} ${name}`,
            context.region?.name,
          ]
            .filter(Boolean)
            .join(", "),
        };

      case "neighborhood":
        return {
          primary: name,
          secondary: [
            context.place?.name,
            context.region?.region_code,
            context.postcode?.name,
          ]
            .filter(Boolean)
            .join(", "),
          tertiary: [
            name,
            context.place?.name,
            context.region?.region_code,
            context.postcode?.name,
          ]
            .filter(Boolean)
            .join(", "),
        };

      default:
        return {
          primary: name,
          secondary: suggestion.place_formatted.replace(/, United States$/, ""),
          tertiary: [
            name,
            suggestion.place_formatted.replace(/, United States$/, ""),
          ]
            .filter(Boolean)
            .join(", "),
        };
    }
  };

  const normalizeString = (value: string | undefined): string | undefined => {
    if (!value) return undefined;

    value = value.trim().replace(/\s+/g, " ");

    return value === "" ? undefined : value;
  };

  const normalizeLocationParams = (params: LocationParams): LocationParams => {
    return {
      coords: params.coords,
      address: normalizeString(params.address),
      city: normalizeString(params.city),
      state: normalizeString(params.state),
      zipCode: normalizeString(params.zipCode),
    };
  };

  const parseLocationDetails = (
    response: MapboxSearchApiRetrieveResponse,
  ): LocationParams => {
    const feature = response.features[0];
    if (!feature) {
      return {};
    }

    const { properties } = feature;
    const { feature_type, name, coordinates, context } = properties;

    const locationParams: LocationParams = {
      coords: coordinates
        ? {
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
          }
        : undefined,
    };

    switch (feature_type) {
      case "address":
      case "street":
        locationParams.address = name;
        break;
      case "postcode":
        break;
      default:
        locationParams.address = name;
    }

    if (context) {
      if (context.place) {
        locationParams.city = context.place.name;
      }

      if (context.region) {
        locationParams.state = context.region.region_code;
      }

      if (context.postcode) {
        locationParams.zipCode = context.postcode.name;
      }
    }

    return normalizeLocationParams(locationParams);
  };

  const handleSearch = useCallback(
    async (value: string) => {
      setSearchValue(value);

      if (!value || value.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await mapboxApi.getSuggestions({
          searchValue: value,
          accessToken: mapBoxConfig?.mapBoxApiKey,
          appInstanceId: appInstanceId,
        });
        setSuggestions(response.suggestions);
      } catch (err) {
        setSuggestions([]);
      }
    },
    [mapBoxConfig?.mapBoxApiKey, appInstanceId],
  );

  const handleSuggestionSelect = useCallback(
    async (suggestion: MapBoxSearchApiSuggestEntry) => {
      try {
        const response = await mapboxApi.getLocationDetails({
          appInstanceId: appInstanceId,
          mapboxId: suggestion.mapbox_id,
          accessToken: mapBoxConfig?.mapBoxApiKey,
        });
        const locationDetails = parseLocationDetails(response);
        onLocationSelect(locationDetails);
        setSearchValue(formatSuggestionDisplay(suggestion).tertiary);
        setSuggestions([]);
      } catch (err) {
        console.error(err);
      }
    },
    [mapboxApi, onLocationSelect],
  );

  const reset = useCallback(() => {
    setSearchValue("");
    setSuggestions([]);
  }, []);

  return {
    searchValue,
    suggestions,
    handleSearch,
    handleSuggestionSelect,
    formatSuggestion: formatSuggestionDisplay,
    reset,
  };
};

export default useMapboxSearch;
